import { useState } from "react";
import { Crown, Hand, MicOff, MonitorUp, Pin, UserPlus, VideoOff, X } from "@fg/icons";
import { cn } from "@/lib/utils";

export interface RosterPerson {
    connectionId: string;
    name: string;
    isYou?: boolean;
    isHost?: boolean;
    isGuest?: boolean;
    micOn: boolean;
    camOn: boolean;
    handRaised: boolean;
    /** When the hand went up (server time) — orders the queue. */
    raisedAt?: string | null;
    sharing: boolean;
    spotlighted?: boolean;
}

export interface RosterWaiting {
    connectionId: string;
    name: string;
    isGuest: boolean;
}

/**
 * Teams' "People" pane. The meeting only showed a participant count; hand
 * raises, mute state and the lobby were scattered over tiles and floating
 * cards. Here: who is waiting (Admit / Deny / Admit all), the raised-hand
 * queue in order, and everyone in the meeting with their mic, camera and
 * presenting state. The host gets Spotlight and Mute per person, with an
 * inline confirm instead of a blocking browser dialog.
 */
export function CallRoster({
    people,
    waiting,
    canAdmit,
    isHost,
    audioOnly,
    onAdmit,
    onDeny,
    onAdmitAll,
    onSpotlight,
    onMute,
    onAddPeople,
    onLowerHand,
    onLowerAll,
    onClose
}: {
    people: RosterPerson[];
    waiting: RosterWaiting[];
    canAdmit: boolean;
    isHost: boolean;
    audioOnly: boolean;
    onAdmit: (connectionId: string) => void;
    onDeny: (connectionId: string) => void;
    onAdmitAll: () => void;
    onSpotlight: (connectionId: string) => void;
    onMute: (connectionId: string) => void;
    onAddPeople?: () => void;
    /** Host: lower one person's hand ("self" lowers your own). */
    onLowerHand?: (connectionId: string) => void;
    /** Host: lower every hand. */
    onLowerAll?: () => void;
    onClose: () => void;
}) {
    const [confirmMute, setConfirmMute] = useState<string | null>(null);
    // First hand up, first in the queue — by the server's time, not tile order.
    const raised = people
        .filter((p) => p.handRaised)
        .sort((a, b) => raisedTime(a) - raisedTime(b));

    return (
        <aside aria-label="Participants" className="flex h-full w-full flex-col bg-zinc-900 sm:w-80">
            <header className="flex h-11 shrink-0 items-center justify-between border-b border-white/10 px-3">
                <span className="text-sm font-semibold">
                    People <span className="font-normal text-white/50">({people.length})</span>
                </span>
                <div className="flex items-center gap-1">
                    {onAddPeople ? (
                        <button
                            type="button"
                            onClick={onAddPeople}
                            className="flex h-8 items-center gap-1.5 rounded-md px-2 text-xs font-medium text-white/90 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                        >
                            <UserPlus className="h-4 w-4" /> Add people
                        </button>
                    ) : null}
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close participants"
                        className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            </header>

            <div className="min-h-0 flex-1 overflow-y-auto">
                {waiting.length > 0 ? (
                    <section className="border-b border-white/10 p-3" aria-label="Waiting in the lobby">
                        <div className="mb-2 flex items-center justify-between">
                            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-amber-300">
                                Waiting in lobby ({waiting.length})
                            </h3>
                            {canAdmit && waiting.length > 1 ? (
                                <button
                                    type="button"
                                    onClick={onAdmitAll}
                                    className="text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                                >
                                    Admit all
                                </button>
                            ) : null}
                        </div>
                        <ul className="space-y-2">
                            {waiting.map((w) => (
                                <li key={w.connectionId} className="flex items-center gap-2">
                                    <Avatar name={w.name} />
                                    <div className="min-w-0 flex-1">
                                        <div className="truncate text-sm">{w.name}</div>
                                        <div className="text-[11px] text-white/50">{w.isGuest ? "Guest · no account" : "Wants to join"}</div>
                                    </div>
                                    {canAdmit ? (
                                        <div className="flex shrink-0 gap-1">
                                            <button
                                                type="button"
                                                onClick={() => onAdmit(w.connectionId)}
                                                className="rounded-md bg-emerald-600 px-2 py-1 text-xs font-medium hover:bg-emerald-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                                            >
                                                Admit
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => onDeny(w.connectionId)}
                                                aria-label={`Deny ${w.name}`}
                                                className="rounded-md bg-white/10 px-2 py-1 text-xs hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                                            >
                                                Deny
                                            </button>
                                        </div>
                                    ) : (
                                        <span className="shrink-0 text-[11px] text-white/40">Waiting</span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </section>
                ) : null}

                {raised.length > 0 ? (
                    <section className="border-b border-white/10 p-3" aria-label="Raised hands">
                        <div className="mb-2 flex items-center justify-between">
                            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-white/60">
                                Raised hands ({raised.length})
                            </h3>
                            {isHost && onLowerAll ? (
                                <button
                                    type="button"
                                    onClick={onLowerAll}
                                    className="text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                                >
                                    Lower all
                                </button>
                            ) : null}
                        </div>
                        <ol className="space-y-1">
                            {raised.map((p, i) => (
                                <li key={p.connectionId} className="flex items-center gap-2 text-sm">
                                    <span className="w-4 text-right text-xs text-white/40">{i + 1}</span>
                                    <Hand className="h-3.5 w-3.5 text-amber-300" aria-hidden />
                                    <span className="min-w-0 flex-1 truncate">{p.isYou ? "You" : p.name}</span>
                                    {p.raisedAt ? (
                                        <time className="shrink-0 text-[11px] text-white/40" dateTime={p.raisedAt}>
                                            {new Date(p.raisedAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                                        </time>
                                    ) : null}
                                    {onLowerHand && (isHost || p.isYou) ? (
                                        <button
                                            type="button"
                                            onClick={() => onLowerHand(p.connectionId)}
                                            aria-label={p.isYou ? "Lower your hand" : `Lower ${p.name}'s hand`}
                                            className="shrink-0 rounded bg-white/10 px-1.5 py-0.5 text-[11px] hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                                        >
                                            Lower
                                        </button>
                                    ) : null}
                                </li>
                            ))}
                        </ol>
                    </section>
                ) : null}

                <section className="p-3" aria-label="In this meeting">
                    <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-white/60">
                        In this meeting ({people.length})
                    </h3>
                    <ul className="space-y-0.5">
                        {people.map((p) => (
                            <li key={p.connectionId} className="group flex items-center gap-2 rounded-md px-1.5 py-1.5 hover:bg-white/5">
                                <Avatar name={p.name} />
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1 text-sm">
                                        <span className="truncate">{p.name}</span>
                                        {p.isYou ? <span className="shrink-0 text-white/50">(you)</span> : null}
                                    </div>
                                    {p.isHost || p.isGuest ? (
                                        <div className="flex items-center gap-1 text-[11px] text-white/50">
                                            {p.isHost ? <Crown className="h-3 w-3 text-amber-300" aria-hidden /> : null}
                                            {p.isHost ? "Organiser" : "Guest"}
                                        </div>
                                    ) : null}
                                </div>
                                <div className="flex shrink-0 items-center gap-1 text-white/60">
                                    {p.handRaised ? <Hand className="h-3.5 w-3.5 text-amber-300" aria-label="Hand raised" /> : null}
                                    {p.sharing ? <MonitorUp className="h-3.5 w-3.5 text-primary" aria-label="Presenting" /> : null}
                                    {!audioOnly && !p.camOn ? <VideoOff className="h-3.5 w-3.5" aria-label="Camera off" /> : null}
                                    {!p.micOn ? <MicOff className="h-3.5 w-3.5 text-red-400" aria-label="Muted" /> : null}
                                </div>
                                {isHost && !p.isYou ? (
                                    confirmMute === p.connectionId ? (
                                        <span className="flex shrink-0 items-center gap-1">
                                            <button
                                                type="button"
                                                onClick={() => { onMute(p.connectionId); setConfirmMute(null); }}
                                                className="rounded bg-red-600 px-1.5 py-0.5 text-[11px] font-medium hover:bg-red-500"
                                            >
                                                Mute
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setConfirmMute(null)}
                                                className="rounded bg-white/10 px-1.5 py-0.5 text-[11px] hover:bg-white/20"
                                            >
                                                Cancel
                                            </button>
                                        </span>
                                    ) : (
                                        <span className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
                                            {!audioOnly ? (
                                                <RowButton
                                                    label={p.spotlighted ? `Stop spotlighting ${p.name}` : `Spotlight ${p.name}`}
                                                    active={p.spotlighted}
                                                    onClick={() => onSpotlight(p.connectionId)}
                                                >
                                                    <Pin className="h-3.5 w-3.5" />
                                                </RowButton>
                                            ) : null}
                                            {p.micOn ? (
                                                <RowButton label={`Mute ${p.name}`} onClick={() => setConfirmMute(p.connectionId)}>
                                                    <MicOff className="h-3.5 w-3.5" />
                                                </RowButton>
                                            ) : null}
                                        </span>
                                    )
                                ) : null}
                            </li>
                        ))}
                    </ul>
                </section>
            </div>
        </aside>
    );
}

function RowButton({ label, active, onClick, children }: {
    label: string;
    active?: boolean;
    onClick: () => void;
    children: React.ReactNode;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={label}
            title={label}
            aria-pressed={active}
            className={cn(
                "flex h-7 w-7 items-center justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                active ? "bg-amber-500/80 text-zinc-900" : "text-white/80 hover:bg-white/10"
            )}
        >
            {children}
        </button>
    );
}

function Avatar({ name }: { name: string }) {
    return (
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-zinc-700 text-xs font-semibold" aria-hidden>
            {initials(name)}
        </span>
    );
}

/** A hand with no server time yet (just raised locally) goes to the back of the queue. */
function raisedTime(p: RosterPerson): number {
    const t = p.raisedAt ? Date.parse(p.raisedAt) : NaN;
    return Number.isFinite(t) ? t : Number.MAX_SAFE_INTEGER;
}

function initials(name: string): string {
    const words = name.replace(/\(guest\)/i, "").split(/\s+/).filter(Boolean);
    if (words.length === 0) return "?";
    if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
    return (words[0]![0]! + words[words.length - 1]![0]!).toUpperCase();
}
